/**
 * Auth service — 2 factores.
 *
 * 1er factor — depende del stage (GET {API}/mfa/auth-config, público):
 *   mode "coredb" (prd — CucuruCoreDBApi, igual que siempre):
 *     POST {ADMIN}/v1/Users/CreateWebOTP   { email, client }    → manda código al mail
 *     POST {ADMIN}/Auth/Login              { uid, pwd, client } → JWT CoreDB (HS384)
 *   mode "cognito" (sandbox/qa — stages sin CoreDB, docs/sandbox-cognito-auth.md):
 *     POST https://cognito-idp.{region}.amazonaws.com/  InitiateAuth USER_PASSWORD_AUTH
 *     (+ RespondToAuthChallenge NEW_PASSWORD_REQUIRED en el primer login) → access token.
 *     Fetch plano, sin SDK: el app client no tiene secret y el client id es público.
 *   En ambos casos el token se guarda en `webadmin2.corejwt` y solo sirve para:
 *   (a) los endpoints /mfa/* de webadmin2, (b) la llamada directa a CoreDB de
 *   bind.ts (que en sandbox falla a propósito: no hay CoreDB).
 *
 * 2do factor (MfaFn de webadmin2, TOTP RFC 6238):
 *   GET  {API}/mfa/status          → { enrolled }
 *   POST {API}/mfa/enroll          → { otpauth_uri, secret } (QR primera vez)
 *   POST {API}/mfa/enroll/confirm  { code } → { token }
 *   POST {API}/mfa/verify          { code } → { token }
 *   El token devuelto (claim mfa:true) se guarda en `webadmin2.jwt` y es el
 *   ÚNICO que aceptan las lambdas de webadmin2.
 */
const ADMIN_API_BASE =
  (import.meta.env.VITE_ADMIN_API as string | undefined) ??
  "https://api.cucuru.com/app";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "http://localhost:3000";

const CLIENT_ID =
  (import.meta.env.VITE_ADMIN_CLIENT as string | undefined) ??
  "ccliweb-nUb1cCp04gi";

const LS_TOKEN      = "webadmin2.jwt";
const LS_CORE_TOKEN = "webadmin2.corejwt";
const LS_EMAIL      = "webadmin2.email";

export function useAuth() {
  const token = localStorage.getItem(LS_TOKEN);
  const email = localStorage.getItem(LS_EMAIL);
  return { token, email };
}

export async function requestOtp(email: string): Promise<void> {
  const res = await fetch(`${ADMIN_API_BASE}/v1/Users/CreateWebOTP`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ email: email.toLowerCase(), client: CLIENT_ID }),
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(`Failed to send OTP: ${res.status} ${msg}`);
  }
  localStorage.setItem(LS_EMAIL, email);
}

export async function verifyOtp(email: string, code: string): Promise<string> {
  const res = await fetch(`${ADMIN_API_BASE}/Auth/Login`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({
      uid:    email.toLowerCase(),
      pwd:    code,
      client: CLIENT_ID,
      type:   "webadmin2",
    }),
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(`Invalid code (${res.status}) ${msg}`);
  }
  const data = await res.json();
  // CoreDBApi AuthController returns { token, version, expires, roles, merchant_id }
  const token: string = data?.token ?? "";
  if (!token) throw new Error("Login response missing token");
  // Strip `Bearer ` prefix if present (LoginV1 returns `Bearer <jwt>`; V2 may not)
  const clean = token.startsWith("Bearer ") ? token.slice(7) : token;
  localStorage.setItem(LS_CORE_TOKEN, clean);
  return clean;
}

// ----- 1er factor: configuración por stage ---------------------------------------

export type AuthMode = "coredb" | "cognito";
export interface AuthConfig {
  mode:          AuthMode;
  region?:       string;
  user_pool_id?: string;
  client_id?:    string;
}

let _authConfig: AuthConfig | null = null;

/** Qué 1er factor usa este stage. Público, cacheado en memoria; si el API no
 *  responde se asume coredb (comportamiento histórico). */
export async function getAuthConfig(): Promise<AuthConfig> {
  if (_authConfig) return _authConfig;
  try {
    const res = await fetch(`${API_BASE}/mfa/auth-config`);
    if (res.ok) {
      const data = await res.json();
      if (data?.mode === "cognito" && data.region && data.client_id) {
        _authConfig = { mode: "cognito", region: data.region, user_pool_id: data.user_pool_id, client_id: data.client_id };
        return _authConfig;
      }
    }
  } catch { /* sin config → coredb */ }
  _authConfig = { mode: "coredb" };
  return _authConfig;
}

// ----- 1er factor: Cognito (USER_PASSWORD_AUTH, fetch plano) ---------------------

export interface CognitoNewPasswordChallenge { challenge: "NEW_PASSWORD_REQUIRED"; session: string }
export type CognitoLoginResult = { ok: true } | CognitoNewPasswordChallenge;

const COGNITO_ERRORS: Record<string, string> = {
  NotAuthorizedException:        "Email o contraseña incorrectos.",
  UserNotFoundException:         "Email o contraseña incorrectos.",
  UserNotConfirmedException:     "El usuario todavía no está confirmado. Pedile a un admin que lo confirme.",
  PasswordResetRequiredException:"Hay que resetear la contraseña. Pedile a un admin que lo haga por CLI.",
  InvalidPasswordException:      "La contraseña no cumple la política: mínimo 12 caracteres con mayúsculas, minúsculas y números.",
  InvalidParameterException:     "Datos inválidos.",
  TooManyRequestsException:      "Demasiados intentos — esperá un momento y probá de nuevo.",
  LimitExceededException:        "Demasiados intentos — esperá un momento y probá de nuevo.",
  ExpiredCodeException:          "La sesión de cambio de contraseña venció. Volvé a ingresar.",
  CodeMismatchException:         "Código incorrecto.",
};

async function cognitoCall(cfg: AuthConfig, target: string, body: Record<string, unknown>): Promise<any> {
  const res = await fetch(`https://cognito-idp.${cfg.region}.amazonaws.com/`, {
    method:  "POST",
    headers: { "Content-Type": "application/x-amz-json-1.1", "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}` },
    body:    JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = String(data.__type ?? data.code ?? "").split("#").pop() ?? "";
    throw new Error(COGNITO_ERRORS[code] ?? `Cognito ${target}: ${code || res.status} ${data.message ?? ""}`.trim());
  }
  return data;
}

function storeCognitoResult(email: string, data: any): CognitoLoginResult {
  if (data?.ChallengeName === "NEW_PASSWORD_REQUIRED") {
    return { challenge: "NEW_PASSWORD_REQUIRED", session: String(data.Session ?? "") };
  }
  if (data?.ChallengeName) {
    throw new Error(`Cognito pidió un desafío no soportado (${data.ChallengeName}). Avisale a un admin.`);
  }
  const token: string = data?.AuthenticationResult?.AccessToken ?? "";
  if (!token) throw new Error("Cognito no devolvió access token");
  localStorage.setItem(LS_CORE_TOKEN, token);
  localStorage.setItem(LS_EMAIL, email.toLowerCase());
  return { ok: true };
}

/** Login usuario+contraseña contra el User Pool del stage. Si Cognito pide
 *  contraseña nueva (primer login con la temporal), devuelve el challenge y
 *  hay que llamar a cognitoSetNewPassword. */
export async function cognitoLogin(email: string, password: string): Promise<CognitoLoginResult> {
  const cfg = await getAuthConfig();
  if (cfg.mode !== "cognito") throw new Error("Este ambiente no usa Cognito");
  const data = await cognitoCall(cfg, "InitiateAuth", {
    AuthFlow: "USER_PASSWORD_AUTH",
    ClientId: cfg.client_id,
    AuthParameters: { USERNAME: email.toLowerCase(), PASSWORD: password },
  });
  return storeCognitoResult(email, data);
}

export async function cognitoSetNewPassword(email: string, session: string, newPassword: string): Promise<CognitoLoginResult> {
  const cfg = await getAuthConfig();
  if (cfg.mode !== "cognito") throw new Error("Este ambiente no usa Cognito");
  const data = await cognitoCall(cfg, "RespondToAuthChallenge", {
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    ClientId: cfg.client_id,
    Session: session,
    ChallengeResponses: { USERNAME: email.toLowerCase(), NEW_PASSWORD: newPassword },
  });
  return storeCognitoResult(email, data);
}

// ----- 2do factor (TOTP) ------------------------------------------------------

function coreAuthHeader(): Record<string, string> {
  const jwt = localStorage.getItem(LS_CORE_TOKEN);
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function mfaFetch(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...coreAuthHeader(), ...(init?.headers ?? {}) },
  });
  if (res.status === 403) throw new Error("Tu cuenta no está habilitada para este panel.");
  if (res.status === 429) throw new Error("Demasiados intentos — esperá 5 minutos y probá de nuevo.");
  return res;
}

export async function mfaStatus(): Promise<{ enrolled: boolean }> {
  const res = await mfaFetch("/mfa/status");
  if (!res.ok) throw new Error(`No se pudo consultar el estado MFA (${res.status})`);
  return res.json();
}

export async function mfaEnroll(): Promise<{ otpauthUri: string; secret: string }> {
  const res = await mfaFetch("/mfa/enroll", { method: "POST" });
  if (!res.ok) throw new Error(`No se pudo iniciar el enrolamiento (${res.status})`);
  const data = await res.json();
  return { otpauthUri: data.otpauth_uri, secret: data.secret };
}

async function mfaTokenRequest(path: string, code: string): Promise<string> {
  const res = await mfaFetch(path, { method: "POST", body: JSON.stringify({ code }) });
  if (!res.ok) throw new Error("Código inválido");
  const data = await res.json();
  const token: string = data?.token ?? "";
  if (!token) throw new Error("MFA response missing token");
  localStorage.setItem(LS_TOKEN, token);
  return token;
}

export const mfaConfirm = (code: string) => mfaTokenRequest("/mfa/enroll/confirm", code);
export const mfaVerify  = (code: string) => mfaTokenRequest("/mfa/verify", code);

export function logout() {
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_CORE_TOKEN);
  localStorage.removeItem(LS_EMAIL);
}
